import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { BarChart3, TrendingUp, TrendingDown, Package, CheckCircle, XCircle, AlertCircle, AlertTriangle, Minus } from 'lucide-react';
import { useAsset, useEquipmentStore } from '../../store/store';
import axiosInstance from '../../common/axiosInstance';

interface EquipmentStats {
  equipmentType: string;
  totalAssets: number;
  assessedAssets: number;
  notAssessedAssets: number;
  assessmentPercentage: number;
  complied: number;
  partiallyComplied: number;
  notComplied: number;
  compliancePercentage: number;
}

interface ComplianceSummary {
  totalComplied: number;
  totalPartiallyComplied: number;
  totalNotComplied: number;
  totalAssessed: number;
  // Question/Response level counts
  totalQuestionsComplied: number;
  totalQuestionsPartiallyComplied: number;
  totalQuestionsNotComplied: number;
  totalQuestionsNotApplicable: number;
  totalQuestions: number;
}

interface EquipmentDashboardProps {
  isVisible: boolean;
  currentEquipmentType?: string;
}

const EquipmentDashboard: React.FC<EquipmentDashboardProps> = ({ isVisible, currentEquipmentType }) => {
  const [stats, setStats] = useState<EquipmentStats[]>([]);
  const [complianceSummary, setComplianceSummary] = useState<ComplianceSummary>({
    totalComplied: 0,
    totalPartiallyComplied: 0,
    totalNotComplied: 0,
    totalAssessed: 0,
    totalQuestionsComplied: 0,
    totalQuestionsPartiallyComplied: 0,
    totalQuestionsNotComplied: 0,
    totalQuestionsNotApplicable: 0,
    totalQuestions: 0
  });
  const [loading, setLoading] = useState(false);
  const { allAssets, getAllAssetsHandler } = useAsset();
  const { equipmentData: equipmentTypes } = useEquipmentStore();

  const equipmentMap = useMemo(() => {
    return new Map(
      equipmentTypes.map((equipment) => [equipment._id, equipment.equipmentName])
    );
  }, [equipmentTypes]);

  const totalQuestionResponses = useMemo(
    () =>
      complianceSummary.totalQuestions +
      complianceSummary.totalQuestionsNotApplicable,
    [complianceSummary]
  );

  const naQuestionPercentage = useMemo(() => {
    if (totalQuestionResponses === 0) {
      return 0;
    }
    return (
      (complianceSummary.totalQuestionsNotApplicable / totalQuestionResponses) * 100
    );
  }, [complianceSummary, totalQuestionResponses]);

  const fetchDashboardStats = useCallback(async () => {
    setLoading(true);
    try {

      if (allAssets.length === 0) {
        await getAllAssetsHandler();
      }

      const assessmentsResponse = await axiosInstance.get('/api/inspection-assessments', {
        params: { limit: 1000 }
      });

      const responseData = assessmentsResponse.data.data;
      const assessments = Array.isArray(responseData) ? responseData : (responseData?.assessments || []);

      const nonDraftAssessments = assessments.filter((assessment: any) => !assessment.isDraft);

      const assessedAssetIds = new Set(
        nonDraftAssessments.map((assessment: any) => assessment.assetId)
      );

      // Map assetId to its latest assessment for compliance calculation
      const latestAssessmentMap = new Map<string, any>();
      nonDraftAssessments.forEach((assessment: any) => {
        const assetId = assessment.assetId || assessment._id;
        const existing = latestAssessmentMap.get(assetId);
        const assessmentDate = new Date(assessment.inspectionDate || assessment.createdAt);
        const existingDate = existing ? new Date(existing.inspectionDate || existing.createdAt) : null;

        if (!existing || !existingDate || assessmentDate > existingDate) {
          latestAssessmentMap.set(assetId, assessment);
        }
      });


      const currentAllAssets = useAsset.getState().allAssets;

      const statsByType = new Map<string, {
        total: number;
        assessed: number;
        complied: number;
        partiallyComplied: number;
        notComplied: number;
      }>();

      const targetName = (currentEquipmentType || '').trim().toLowerCase();

      currentAllAssets.forEach((asset: any) => {
        const equipmentName = equipmentMap.get(asset.equipmentInventory) || 'Unknown';
        const equipmentNameLc = (equipmentName || '').toString().trim().toLowerCase();

        if (targetName && equipmentNameLc !== targetName) {
          return;
        }

        if (!statsByType.has(equipmentName)) {
          statsByType.set(equipmentName, {
            total: 0,
            assessed: 0,
            complied: 0,
            partiallyComplied: 0,
            notComplied: 0
          });
        }

        const currentStats = statsByType.get(equipmentName)!;
        currentStats.total += 1;

        const assetId = asset._id || asset.id;
        if (assessedAssetIds.has(assetId)) {
          currentStats.assessed += 1;

          // Get the latest assessment for this asset to determine compliance
          const latestAssessment = latestAssessmentMap.get(assetId);
          if (latestAssessment) {
            const overallStatus = latestAssessment.overallStatus?.toLowerCase();
            if (overallStatus === 'pass') {
              currentStats.complied += 1;
            } else if (overallStatus === 'attention') {
              currentStats.partiallyComplied += 1;
            } else if (overallStatus === 'fail') {
              currentStats.notComplied += 1;
            }
          }
        }
      });

      const statsArray: EquipmentStats[] = Array.from(statsByType.entries()).map(([equipmentType, data]) => {
        const totalAssessed = data.assessed;
        const compliancePercentage = totalAssessed > 0
          ? Math.round((data.complied / totalAssessed) * 100)
          : 0;

        return {
          equipmentType,
          totalAssets: data.total,
          assessedAssets: data.assessed,
          notAssessedAssets: data.total - data.assessed,
          assessmentPercentage: data.total > 0 ? Math.round((data.assessed / data.total) * 100) : 0,
          complied: data.complied,
          partiallyComplied: data.partiallyComplied,
          notComplied: data.notComplied,
          compliancePercentage
        };
      });

      statsArray.sort((a, b) => b.totalAssets - a.totalAssets);
      // Calculate overall compliance summary at asset level
      const assetLevelSummary = statsArray.reduce(
        (acc, stat) => ({
          totalComplied: acc.totalComplied + stat.complied,
          totalPartiallyComplied: acc.totalPartiallyComplied + stat.partiallyComplied,
          totalNotComplied: acc.totalNotComplied + stat.notComplied,
          totalAssessed: acc.totalAssessed + stat.assessedAssets
        }),
        { totalComplied: 0, totalPartiallyComplied: 0, totalNotComplied: 0, totalAssessed: 0 }
      );

      // Calculate question/response level compliance across all latest assessments
      let totalQuestionsComplied = 0;
      let totalQuestionsPartiallyComplied = 0;
      let totalQuestionsNotComplied = 0;
      let totalQuestionsNotApplicable = 0;
      let totalQuestions = 0;

      latestAssessmentMap.forEach((assessment) => {
        if (assessment.responses && Array.isArray(assessment.responses)) {
          assessment.responses.forEach((response: any) => {
            const responseStatus = response.status?.toLowerCase();
            if (responseStatus === 'pass') {
              totalQuestionsComplied += 1;
            } else if (responseStatus === 'attention') {
              totalQuestionsPartiallyComplied += 1;
            } else if (responseStatus === 'fail') {
              totalQuestionsNotComplied += 1;
            } else if (responseStatus === 'na') {
              totalQuestionsNotApplicable += 1;
            }
            // Count all responses (excluding 'na' if needed)
            if (responseStatus && responseStatus !== 'na') {
              totalQuestions += 1;
            }
          });
        }
      });

      const summary: ComplianceSummary = {
        ...assetLevelSummary,
        totalQuestionsComplied,
        totalQuestionsPartiallyComplied,
        totalQuestionsNotComplied,
        totalQuestionsNotApplicable,
        totalQuestions
      };

      setStats(statsArray);
      setComplianceSummary(summary);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  }, [allAssets, equipmentMap, currentEquipmentType, getAllAssetsHandler]);

  useEffect(() => {
    if (isVisible) {
      fetchDashboardStats();
    }
  }, [isVisible, currentEquipmentType, fetchDashboardStats]);

  if (!isVisible) {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-6 h-6 text-[#00A8A5]" />
          <h2 className="text-xl font-semibold text-gray-800">Equipment Assessment Dashboard</h2>
        </div>
        {/* <div className="text-sm text-gray-500">
          Last updated: {new Date().toLocaleTimeString()}
        </div> */}
      </div>

      {stats.length === 0 ? (
        <div className="text-center py-8">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No equipment data available</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-semibold text-gray-800 text-sm leading-tight">
                  {stat.equipmentType}
                </h3>
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${stat.assessmentPercentage >= 80
                  ? 'bg-green-100 text-green-800'
                  : stat.assessmentPercentage >= 50
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
                  }`}>
                  {stat.assessmentPercentage}%
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-gray-600" />
                    <span className="text-sm text-gray-600">Total</span>
                  </div>
                  <span className="font-semibold text-gray-800">{stat.totalAssets}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-gray-600">Assessed</span>
                  </div>
                  <span className="font-semibold text-green-700">{stat.assessedAssets}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-600" />
                    <span className="text-sm text-gray-600">Not Assessed</span>
                  </div>
                  <span className="font-semibold text-red-700">{stat.notAssessedAssets}</span>
                </div>
              </div>

              <div className="mt-3">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${stat.assessmentPercentage >= 80
                      ? 'bg-green-500'
                      : stat.assessmentPercentage >= 50
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                      }`}
                    style={{ width: `${stat.assessmentPercentage}%` }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {stats.length > 0 && (
        <>
          {/* Compliance Status Chart */}
          {complianceSummary.totalAssessed > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <AlertCircle className="w-5 h-5 text-[#00A8A5]" />
                <h3 className="text-lg font-semibold text-gray-800">Compliance Status Overview</h3>
              </div>

              {/* Summary Info */}
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Out of {stats.reduce((sum, stat) => sum + stat.totalAssets, 0)} total assets</span>
                  {', '}
                  <span className="font-semibold text-green-600">{complianceSummary.totalAssessed} assessed</span>
                  {', '}
                  and in those {complianceSummary.totalAssessed} assets' assessments:
                  <span className="font-semibold text-green-700 ml-1">{complianceSummary.totalQuestionsComplied}</span> questions complied,
                  <span className="font-semibold text-red-700 ml-1">{complianceSummary.totalQuestionsNotComplied}</span> not complied,
                  <span className="font-semibold text-yellow-700 ml-1">{complianceSummary.totalQuestionsPartiallyComplied}</span> partially complied
                  {complianceSummary.totalQuestionsNotApplicable > 0 && (
                    <span>{','}<span className="font-semibold text-gray-600 ml-1">{complianceSummary.totalQuestionsNotApplicable}</span> marked N/A</span>
                  )}
                  {complianceSummary.totalQuestions > 0 && (
                    <span className="text-gray-600 ml-1">
                      (out of {complianceSummary.totalQuestions} scored questions)
                    </span>
                  )}
                </p>
              </div>


              {/* Question/Response Level Compliance */}
              {(complianceSummary.totalQuestions > 0 || complianceSummary.totalQuestionsNotApplicable > 0) && (
                <div className="mb-6">
                  <h4 className="text-md font-semibold text-gray-700 mb-4">Question Level Compliance</h4>
                  <div className={`grid grid-cols-1 ${complianceSummary.totalQuestionsNotApplicable > 0 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3'
                    } gap-4`}>
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-5 border border-green-200 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="text-sm font-medium text-gray-700">Questions Complied</span>
                        </div>
                        <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full">
                          {complianceSummary.totalQuestions > 0
                            ? Math.round((complianceSummary.totalQuestionsComplied / complianceSummary.totalQuestions) * 100)
                            : 0}%
                        </span>
                      </div>
                      <div className="text-3xl font-bold text-green-700 mb-2">
                        {complianceSummary.totalQuestionsComplied}
                      </div>
                      <div className="text-xs text-gray-600">
                        out of {complianceSummary.totalQuestions} total questions
                      </div>
                      <div className="mt-3">
                        <div className="w-full bg-green-100 rounded-full h-2">
                          <div
                            className="bg-green-500 h-2 rounded-full transition-all duration-300"
                            style={{
                              width: `${complianceSummary.totalQuestions > 0
                                ? (complianceSummary.totalQuestionsComplied / complianceSummary.totalQuestions) * 100
                                : 0}%`
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-lg p-5 border border-yellow-200 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5 text-yellow-600" />
                          <span className="text-sm font-medium text-gray-700">Questions Partially Complied</span>
                        </div>
                        <span className="text-xs font-medium text-yellow-700 bg-yellow-100 px-2 py-1 rounded-full">
                          {complianceSummary.totalQuestions > 0
                            ? Math.round((complianceSummary.totalQuestionsPartiallyComplied / complianceSummary.totalQuestions) * 100)
                            : 0}%
                        </span>
                      </div>
                      <div className="text-3xl font-bold text-yellow-700 mb-2">
                        {complianceSummary.totalQuestionsPartiallyComplied}
                      </div>
                      <div className="text-xs text-gray-600">
                        out of {complianceSummary.totalQuestions} total questions
                      </div>
                      <div className="mt-3">
                        <div className="w-full bg-yellow-100 rounded-full h-2">
                          <div
                            className="bg-yellow-500 h-2 rounded-full transition-all duration-300"
                            style={{
                              width: `${complianceSummary.totalQuestions > 0
                                ? (complianceSummary.totalQuestionsPartiallyComplied / complianceSummary.totalQuestions) * 100
                                : 0}%`
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-lg p-5 border border-red-200 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <XCircle className="w-5 h-5 text-red-600" />
                          <span className="text-sm font-medium text-gray-700">Questions Not Complied</span>
                        </div>
                        <span className="text-xs font-medium text-red-700 bg-red-100 px-2 py-1 rounded-full">
                          {complianceSummary.totalQuestions > 0
                            ? Math.round((complianceSummary.totalQuestionsNotComplied / complianceSummary.totalQuestions) * 100)
                            : 0}%
                        </span>
                      </div>
                      <div className="text-3xl font-bold text-red-700 mb-2">
                        {complianceSummary.totalQuestionsNotComplied}
                      </div>
                      <div className="text-xs text-gray-600">
                        out of {complianceSummary.totalQuestions} total questions
                      </div>
                      <div className="mt-3">
                        <div className="w-full bg-red-100 rounded-full h-2">
                          <div
                            className="bg-red-500 h-2 rounded-full transition-all duration-300"
                            style={{
                              width: `${complianceSummary.totalQuestions > 0
                                ? (complianceSummary.totalQuestionsNotComplied / complianceSummary.totalQuestions) * 100
                                : 0}%`
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    {complianceSummary.totalQuestionsNotApplicable > 0 && (
                      <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-5 border border-slate-200 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Minus className="w-5 h-5 text-slate-600" />
                            <span className="text-sm font-medium text-gray-700">Questions Marked N/A</span>
                          </div>
                          <span className="text-xs font-medium text-slate-700 bg-slate-100 px-2 py-1 rounded-full">
                            {Math.round(naQuestionPercentage)}%
                          </span>
                        </div>
                        <div className="text-3xl font-bold text-slate-700 mb-2">
                          {complianceSummary.totalQuestionsNotApplicable}
                        </div>
                        <div className="text-xs text-gray-600">
                          out of {totalQuestionResponses} total responses
                        </div>
                        <div className="mt-3">
                          <div className="w-full bg-slate-100 rounded-full h-2">
                            <div
                              className="bg-slate-500 h-2 rounded-full transition-all duration-300"
                              style={{
                                width: `${naQuestionPercentage}%`
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Assessment Summary */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {stats.reduce((sum, stat) => sum + stat.totalAssets, 0)}
                </div>
                <div className="text-sm text-gray-600">Total Assets</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {stats.reduce((sum, stat) => sum + stat.assessedAssets, 0)}
                </div>
                <div className="text-sm text-gray-600">Total Assessed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  {stats.reduce((sum, stat) => sum + stat.notAssessedAssets, 0)}
                </div>
                <div className="text-sm text-gray-600">Total Not Assessed</div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EquipmentDashboard;
